/**
 * Crash diagnostics that actually reach Google.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 *
 * Google Play rejected v1.9.0 under Broken Functionality — "the app opens, but
 * it keeps crashing" — and we could not produce a single stack trace. Two
 * separate reasons, both fixed here:
 *
 * 1. `client/sentry.ts` only initialises when `EXPO_PUBLIC_SENTRY_DSN` is set,
 *    and it is set nowhere. So Sentry is inert in the shipped build.
 * 2. `ErrorBoundary.componentDidCatch` built a full `errorLog` object — message,
 *    stack, componentStack — and then emitted NOTHING. Three `console.error`
 *    calls had been stripped to `/* production: no log *\/`, and its only other
 *    sink was `localStorage`, which DOES NOT EXIST in React Native, so the
 *    `try/catch` swallowed a ReferenceError and persisted nothing. A caught
 *    React error produced zero diagnostics anywhere on Android.
 *
 * ─── What Google actually wants ─────────────────────────────────────────────
 *
 * Play Console collects crashes through Android vitals, and a crash report
 * carries the device's **logcat** output from the moments before the crash. So
 * `console.error` is not a fallback here — on Android it is the channel that
 * puts a readable JS stack in front of a Google reviewer, with no third-party
 * account required. Hermes' own native trace is unreadable frames; this is the
 * part a human can act on.
 *
 * Every line is prefixed with `CRASH_TAG` so it can be grepped out of a noisy
 * logcat: `adb logcat | grep FAHALI_CRASH`.
 *
 * ─── Two rules this module must never break ─────────────────────────────────
 *
 * 1. NEVER THROW. It runs on the crash path; throwing here turns a recoverable
 *    error into a second crash, or hides the first.
 * 2. NEVER SWALLOW THE CRASH. A fatal JS error must still reach React Native's
 *    default handler, because that is what converts it into a native crash —
 *    which is what Android vitals actually counts. A handler that "handles" a
 *    fatal error makes the app look healthy to Google while it dies for users,
 *    which is strictly worse than no handler at all.
 */

/** Greppable marker. `adb logcat | grep FAHALI_CRASH`. */
export const CRASH_TAG = "[FAHALI_CRASH]";

export type CrashOrigin = "global" | "react-render" | "manual";

export interface CrashContext {
  /** Fatal errors kill the app; non-fatal ones are recoverable but still reported. */
  fatal: boolean;
  origin: CrashOrigin;
  /** React's component stack, when the error came from a render. */
  componentStack?: string;
}

/**
 * Render one self-contained, greppable block for logcat.
 *
 * Pure and dependency-free so it can be tested without a device, a bundler, or
 * Sentry — the same reason `server/lib/crashAlertRow.ts` was extracted.
 *
 * Accepts `unknown` deliberately: JS lets you throw a string, a number, or
 * `undefined`, and the global handler receives whatever was thrown. Assuming
 * `Error` here would throw ON THE CRASH PATH, violating rule 1.
 */
export function formatCrashLog(error: unknown, context: CrashContext): string {
  let message: string;
  let stack: string;

  if (error instanceof Error) {
    message = error.message || "(empty message)";
    stack = error.stack || "(no stack captured)";
  } else {
    // Non-Error throws carry no stack. Say so rather than printing "undefined",
    // which reads like a missing field instead of an absent one.
    message = safeStringify(error);
    stack = "(no stack — a non-Error value was thrown)";
  }

  const lines = [
    `${CRASH_TAG} ${context.fatal ? "FATAL" : "NON-FATAL"} origin=${context.origin}`,
    `message: ${message}`,
    `stack:`,
    stack,
  ];

  if (context.componentStack) {
    lines.push("componentStack:", context.componentStack);
  }

  return lines.join("\n");
}

/** Never throws, never returns undefined — see rule 1. */
function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // Circular structures, and objects with a throwing toJSON, both land here.
    try {
      return String(value);
    } catch {
      return "(unstringifiable thrown value)";
    }
  }
}

/**
 * Emit the block. Separated from formatting so callers can be tested against a
 * fake sink, and so there is exactly ONE place that decides where crash text
 * goes.
 */
export function emitCrashLog(
  error: unknown,
  context: CrashContext,
  sink: (text: string) => void = console.error,
): void {
  try {
    sink(formatCrashLog(error, context));
  } catch {
    // Rule 1. A logging failure must not escalate.
  }
}

/**
 * Minimal shape of React Native's `ErrorUtils` global.
 *
 * Declared structurally, and every member optional, because this module is also
 * evaluated on web and under the node test runner, where the global is absent.
 * Reaching for the react-native type here would make a pure module depend on
 * the native runtime.
 */
interface ErrorUtilsLike {
  getGlobalHandler?: () => ((error: unknown, isFatal?: boolean) => void) | undefined;
  setGlobalHandler?: (handler: (error: unknown, isFatal?: boolean) => void) => void;
}

/**
 * Install the process-wide JS error handler.
 *
 * This is the gap `ErrorBoundary` cannot cover: a React error boundary only
 * catches errors thrown during a React render. The crash that got us rejected
 * — a native module failing `TurboModuleRegistry.getEnforcing()` at import time
 * — happens before any component renders, so no boundary can see it. So do
 * async rejections, event-handler throws, and native-module init failures.
 *
 * Returns `true` if installed, `false` where the global does not exist (web,
 * tests). Callers should not treat `false` as an error.
 */
export function installGlobalCrashHandler(
  onCrash: (error: unknown, context: CrashContext) => void,
  errorUtils: ErrorUtilsLike | undefined = (
    globalThis as { ErrorUtils?: ErrorUtilsLike }
  ).ErrorUtils,
): boolean {
  if (!errorUtils?.setGlobalHandler) return false;

  const previous = errorUtils.getGlobalHandler?.();

  errorUtils.setGlobalHandler((error: unknown, isFatal?: boolean) => {
    try {
      onCrash(error, { fatal: Boolean(isFatal), origin: "global" });
    } catch {
      // Rule 1.
    }
    // RULE 2 — this call is load-bearing. RN's default handler is what turns a
    // fatal JS error into a native crash, and a native crash is what Play's
    // Android vitals counts. Drop this and Google sees a perfectly healthy app
    // that users watch die.
    try {
      previous?.(error, isFatal);
    } catch {
      // If the previous handler throws we still must not throw ourselves.
    }
  });

  return true;
}
